import { useState, useEffect } from 'react';
import { Share2 } from 'lucide-react';
import api from '../lib/api';
import axios from 'axios';
import { createShareCard, copyShareLink } from '../lib/share';
import EmailCaptureGate, { TeaserResult } from '../components/EmailCaptureGate';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

export default function GamblerPersonalityQuiz() {
  const [questions, setQuestions] = useState([]);
  const [current, setCurrent] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [result, setResult] = useState(null);
  const [teaserResult, setTeaserResult] = useState(null);
  const [showEmailGate, setShowEmailGate] = useState(false);
  const [loading, setLoading] = useState(true);
  const [sharing, setSharing] = useState(false);

  const token = localStorage.getItem('casinoiq_token');
  const isGuest = !token;

  useEffect(() => {
    const fetchQuestions = async () => {
      try {
        let res;
        if (isGuest) {
          res = await axios.get(`${API_BASE}/api/tools/quiz/gambler-personality`);
        } else {
          res = await api.get('/tools/quiz/gambler-personality');
        }
        setQuestions(res.data.questions || []);
      } catch (e) {
        console.error('Failed to load questions');
      } finally {
        setLoading(false);
      }
    };
    fetchQuestions();
  }, [isGuest]);

  const handleAnswer = async (optionIndex) => {
    const newAnswers = [...answers, { question_id: questions[current].id, option_index: optionIndex }];
    setAnswers(newAnswers);

    if (current + 1 < questions.length) {
      setCurrent(current + 1);
    } else {
      if (isGuest) {
        setShowEmailGate(true);
      } else {
        try {
          const res = await api.post('/tools/quiz/gambler-personality/submit', { answers: newAnswers });
          setResult(res.data);
        } catch (e) {
          console.error('Submit failed');
        }
      }
    }
  };

  const handleEmailSubmit = async (email) => {
    const res = await axios.post(`${API_BASE}/api/tools/quiz/gambler-personality/guest-submit`, {
      answers, email,
    });
    setShowEmailGate(false);
    setTeaserResult(res.data);
  };

  const handleShare = async () => {
    if (!result) return;
    setSharing(true);
    const card = await createShareCard('gambler_personality', {
      archetype_key: result.archetype_key,
      archetype_name: result.archetype.name,
      percentage: result.percentage,
      share_text: result.share_text,
    });
    if (card) copyShareLink(card.share_url);
    setSharing(false);
  };

  if (loading) return <div className="p-8 text-center text-white/50">Loading quiz...</div>;

  if (teaserResult) {
    return <TeaserResult toolType="quiz" archetypeName={teaserResult.archetype_name} percentage={teaserResult.percentage} />;
  }

  if (showEmailGate) {
    return <EmailCaptureGate toolName="Gambler Personality Quiz" onSubmit={handleEmailSubmit} />;
  }

  if (result) {
    const arch = result.archetype;
    return (
      <div className="max-w-2xl mx-auto p-6 space-y-6">
        <div className="bg-[#1e2438] border border-white/10 rounded-2xl p-8 text-center">
          {arch.emoji && <div className="text-5xl mb-3">{arch.emoji}</div>}
          <p className="text-amber-400 text-sm font-medium mb-1">Your Gambler Personality</p>
          <h2 className="text-3xl font-bold text-white mb-2" data-testid="quiz-result-name">{arch.emoji ? `${arch.emoji} ${arch.name}` : arch.name}</h2>
          <p className="text-white/50 text-sm mb-4">Only {result.percentage}% of players share this type</p>
          <p className="text-white/70 text-sm leading-relaxed">{arch.description}</p>
        </div>
        {(arch.recommended_tools || []).length > 0 && (
          <div className="bg-[#1e2438] border border-white/10 rounded-2xl p-6">
            <h3 className="text-white font-semibold mb-3 text-sm">Recommended Tools</h3>
            <div className="flex flex-wrap gap-2">
              {arch.recommended_tools.map((tool, i) => (
                <span key={i} className="px-3 py-1.5 bg-amber-500/10 text-amber-400 rounded-full text-xs">{tool}</span>
              ))}
            </div>
          </div>
        )}
        <div className="bg-[#1e2438] border border-white/10 rounded-2xl p-6">
          <h3 className="text-white font-semibold mb-3 text-sm">Your Scores</h3>
          {Object.entries(result.scores || {}).map(([key, val]) => (
            <div key={key} className="flex items-center justify-between mb-2">
              <span className="text-white/60 text-xs capitalize">{key.replace(/_/g, ' ')}</span>
              <div className="flex items-center gap-2">
                <div className="w-32 h-1.5 bg-white/10 rounded-full overflow-hidden">
                  <div className="h-full bg-amber-500 rounded-full" style={{ width: `${Math.min(100, val * 10)}%` }} />
                </div>
                <span className="text-white/80 text-xs w-6 text-right">{val}</span>
              </div>
            </div>
          ))}
        </div>
        <div className="flex gap-3">
          <button
            data-testid="quiz-share-btn"
            onClick={handleShare}
            disabled={sharing}
            className="flex-1 h-11 rounded-xl border border-white/10 text-white/70 text-sm hover:bg-white/5 flex items-center justify-center gap-2 transition-colors"
          >
            <Share2 className="h-4 w-4" /> {sharing ? 'Creating link...' : 'Share Your Result'}
          </button>
        </div>
      </div>
    );
  }

  if (!questions.length) return <div className="p-8 text-center text-white/50">No questions available</div>;

  const q = questions[current];
  return (
    <div className="max-w-2xl mx-auto p-6">
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <h1 className="text-xl font-bold text-white">What Type of Gambler Are You?</h1>
          <span className="text-white/40 text-sm">{current + 1}/{questions.length}</span>
        </div>
        <div className="w-full h-1.5 bg-white/10 rounded-full overflow-hidden">
          <div className="h-full bg-amber-500 rounded-full transition-all" style={{ width: `${((current + 1) / questions.length) * 100}%` }} />
        </div>
      </div>
      <div className="bg-[#1e2438] border border-white/10 rounded-2xl p-6 mb-4">
        <h2 className="text-lg font-semibold text-white mb-4" data-testid="quiz-question-text">{q.text}</h2>
        <div className="space-y-3">
          {q.options.map((opt, i) => (
            <button
              key={i}
              data-testid={`quiz-option-${i}`}
              onClick={() => handleAnswer(i)}
              className="w-full text-left px-4 py-3 bg-[#141824] border border-white/10 hover:border-amber-500/50 rounded-xl text-white/80 hover:text-white transition-colors text-sm"
            >
              {opt.text}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
